import { fromUsers, toTeams } from 'utils/serializers'
import { timezoneDate, utcDate } from 'utils/helpers'
import moment from 'moment-timezone'
import { getDateFormat, getTimezone } from 'utils/authService'
import { EditorState, convertFromRaw } from 'draft-js'
import { stateToHTML } from 'draft-js-export-html'

const dayToMilliseconds = 24 * 60 * 60 * 1000

export const toCalendarTasks = tasks => {
  return tasks.map(task => ({
    id: task.id,
    title: (task.dealName ? task.dealName + ' - ' : '') + task.subject,
    start: moment(timezoneDate(task.startDate, 'HH:mm:ss'), `${getDateFormat()} HH:mm:ss`).toDate(),
    end: moment(timezoneDate(task.dueDate, 'HH:mm:ss'), `${getDateFormat()} HH:mm:ss`).toDate(),
    task,
    allDay:
      moment(task.dueDate).diff(moment(task.startDate)) >= dayToMilliseconds &&
      moment(task.startDate, 'hh:mm a') === moment(task.dueDate, 'hh:mm a'),
    type: 'task'
  }))
}

export const fromEvent = event => {
  const description =
    event.description !== null && event.description.trim()
      ? EditorState.createWithContent(convertFromRaw(JSON.parse(event.description)))
      : ''

  return {
    subject: event.title,
    startDate: utcDate(event.start),
    dueDate: utcDate(event.end),
    location: event.location,
    description: event.description,
    htmlDescription: description ? stateToHTML(description.getCurrentContent()) : '',
    textDescription: description ? description.getCurrentContent().getPlainText() : '',
    userIds: event.users.map(user => user.value),
    contactIds: event.contacts.map(contact => contact.value),
    timezone: getTimezone(),
    sendInviteEmail: event.sendInviteEmail,
    ...(event.reminders.value
      ? { reminder: event.notifications.time, reminderType: event.notifications.type.value }
      : {})
  }
}

export const toCalendarEvents = events => {
  return events.map(event => ({
    id: event.id,
    title: event.subject,
    status: event.status,
    location: event.location,
    description: event.description,
    start: moment(
      timezoneDate(event.startDate, 'HH:mm:ss'),
      `${getDateFormat()} HH:mm:ss`
    ).toDate(),
    end: moment(timezoneDate(event.dueDate, 'HH:mm:ss'), `${getDateFormat()} HH:mm:ss`).toDate(),
    users: fromUsers(event.attendees.filter(a => a.user).map(a => a.user)).map(u => ({
      ...u,
      type: 'users'
    })),
    contacts: fromUsers(event.attendees.filter(a => a.contact).map(a => a.contact)).map(u => ({
      ...u,
      type: 'contacts'
    })),
    createdBy: event.createdBy,
    allDay:
      moment(event.dueDate).diff(moment(event.startDate)) >= dayToMilliseconds &&
      moment(event.startDate).format('hh:mm a') === moment(event.dueDate).format('hh:mm a'),
    type: 'event',
    notifications: {
      time: event.reminder || 10,
      type: event.reminderType || { value: 'MINUTE', label: 'Minutes' }
    },
    reminders: event.reminder
      ? {
          value: true,
          label: 'Reminder'
        }
      : {
          value: false,
          label: 'No Reminder'
        }
  }))
}

export const toCalendarDeals = (deals, isFollowUp = false) =>
  deals.map(deal => {
    return {
      id: deal.id,
      title: `${toTeams(deal.teams)
        .map(t => t.abbreviation)
        .join(' ')} ${deal.name}${deal.clientName ? '- ' + deal.clientName : ''} -${
        deal.dealManager?.firstName
      } ${deal.dealManager?.lastName?.charAt(0)}`,
      status: deal.status,
      start: moment(
        timezoneDate(isFollowUp ? deal.followUpDate : deal.dueDate, 'HH:mm:ss'),
        `${getDateFormat()} HH:mm:ss`
      ).toDate(),
      end: moment(
        timezoneDate(isFollowUp ? deal.followUpDate : deal.dueDate, 'HH:mm:ss'),
        `${getDateFormat()} HH:mm:ss`
      ).toDate(),
      allDay: true,
      dealManager: deal.dealManager,
      dealName: deal.name,
      type: isFollowUp ? 'followup' : 'bid',
      hardBid: deal.hardBid,
      sealedBid: deal.sealedBid
    }
  })

export const toCalendarLeads = (leads, users) => {
  return leads.map(lead => {
    const assignee = users.find(u => u.id === lead.assigneeId)
    return {
      id: lead.id,
      title: `${lead.name}${assignee ? ' - ' + assignee?.firstName : ''} ${
        assignee ? assignee?.lastName?.charAt(0) : ''
      }`,
      start: moment(
        timezoneDate(lead.followUpDate, 'HH:mm:ss'),
        `${getDateFormat()} HH:mm:ss`
      ).toDate(),
      end: moment(
        timezoneDate(lead.followUpDate, 'HH:mm:ss'),
        `${getDateFormat()} HH:mm:ss`
      ).toDate(),
      allDay: true,
      leadName: lead.name,
      type: 'lead'
    }
  })
}
